import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import CallDialer from '../callDialer'
import {
    Avatar,
    IconButton,
    Toolbar,
    Tooltip,
    Typography,
    Paper,
} from "@mui/material"
import PersonIcon from '@mui/icons-material/Person'
import VoicemailIcon from '@mui/icons-material/Voicemail';
import DialpadIcon from '@mui/icons-material/Dialpad';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';
import AddIcCallIcon from '@mui/icons-material/AddIcCall'
import CallMergeIcon from '@mui/icons-material/CallMerge';
import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import CloseIcon from '@mui/icons-material/Close';
import MicIcon from '@mui/icons-material/Mic'
import MicOffIcon from '@mui/icons-material/MicOff'
import CallIcon from '@mui/icons-material/Call'
import CallEndIcon from '@mui/icons-material/CallEnd'
import DotIcon from '@mui/icons-material/FiberManualRecord';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallMissedIcon from '@mui/icons-material/CallMissed';
import CallMissedOutgoingIcon from '@mui/icons-material/CallMissedOutgoing';
import ButtonBase from '@mui/material/ButtonBase';
import ListParty from '../listParty';
import { formatWhen } from '../../utils/format';
import { formatUsPhone, findContactByNumber, contactDisplayName, dialDigits } from '../../utils/contacts';
import { getPhoneBookEntry } from '../../utils/common';
import SquareIcon from '@mui/icons-material/StopSharp';
import { useStyles } from "./styles"
import clsx from "clsx"
import { WebRtcContextJsSIP } from "../../index"
import {
    RINGING, CONNECTED, INCOMING, ON_HOLD,
    isVoicemail, statusText, sessionName, sessionNumber, callTime, resolveSessionNames,
} from "../../utils/callSessions"
import { initialsOf } from "../ContactAvatar"
import {
    setStartConference,
    setStartTransfer,
} from "../../store/features/user/userSlice";


const mapDispatchToProps = {
    setStartConference,
    setStartTransfer,
};

const Phone = (props) => {
    const {
        title,
        voip,
        sipCalling,
        calling,
        setSipCalling,
        setCalling,
        sipMode,
        options,
        value,
        setValue,
        viewValue,
        setViewValue,
        controller,
        handleSipDialClick,
        handleDialClick,
        deviceSelection,
        setDeviceSelection,
        selectedIndex,
        setSelectedIndex,
        sipSessions: rawSipSessions,
        startConference,
        setStartConference,
        startTransfer,
        setStartTransfer,
        history,
        calls,
        phonebook,
        personalPhonebook,
        externalPhonebook,
    } = props;

    const navigate = useNavigate();

    // Calls with the name of the contact their number belongs to, whichever
    // phonebook it is in.
    const sipSessions = resolveSessionNames(rawSipSessions, [personalPhonebook, phonebook, externalPhonebook]);

    const { classes, cx } = useStyles();
    const [forceDialPad, setForceDialpad] = useState(false);

    const webRtcContextJsSIP = React.useContext(WebRtcContextJsSIP)
    const inConference = webRtcContextJsSIP.getInConference()

    // Colour family of a call state (ring, dot and pill use it).
    const toneClass = (status) =>
        status === CONNECTED ? classes.toneActive
            : status === ON_HOLD ? classes.toneHold
                : status === INCOMING ? classes.toneIncoming
                    : classes.toneCalling;

    const isSettingUp = (status) => status <= RINGING || status === INCOMING;

    const renderSessionAvatar = (session, central) => {
        const avatarClass = cx(
            central ? classes.centralAvatar : classes.avatar,
            central && isSettingUp(session.sessionStatus) && classes.pulsing,
        );
        if (session.contact && session.contact.profileImage) {
            return <img className={avatarClass} src={session.contact.profileImage} alt={sessionName(session)} />;
        }
        if (session.contact) {
            return (
                <Avatar className={avatarClass} src={null}>
                    {`${(session.contact.firstName || "").charAt(0)}${(session.contact.lastName || "").charAt(0)}`}
                </Avatar>
            );
        }
        // A personal or external contact, known by name only: its initials.
        if (session.lookupName && initialsOf(session.lookupName) !== "") {
            return <Avatar className={avatarClass} src={null}>{initialsOf(session.lookupName)}</Avatar>;
        }
        const Icon = isVoicemail(session.remoteNumber) ? VoicemailIcon : PersonIcon;
        return (
            <Avatar className={avatarClass}>
                <Icon className={central ? classes.centralAvatarIcon : classes.avatarIcon} />
            </Avatar>
        );
    };

    let activeSession = sipSessions.find((session) => session.sessionStatus <= CONNECTED)
    let inactiveSessions = []
    let conferenceSessions = []

    // find one session to set as active
    sipSessions.forEach((session) => {
        if (inConference && (session.sessionStatus === CONNECTED || session.sessionStatus === ON_HOLD)) {
            conferenceSessions.push(session);
            return
        }

        if (!activeSession) {
            activeSession = session
        }

        if (session !== activeSession || startConference || startTransfer || forceDialPad) {
            inactiveSessions.push(session)
        }
    })

    // Re-render once a second while a call is connected, for the call timer.
    const hasTimedCall = sipSessions.some((session) => session.connectedAt);
    const [, setTick] = useState(0);
    useEffect(() => {
        if (!hasTimedCall) return undefined;
        const id = window.setInterval(() => setTick((tick) => tick + 1), 1000);
        return () => window.clearInterval(id);
    }, [hasTimedCall]);

    const dialerInputCb = (input) => {
        if (inConference) {
            conferenceSessions.forEach((session) => webRtcContextJsSIP.sendDTMF(session.remoteNumber, input));
        } else if (activeSession?.sessionStatus === CONNECTED) {
            webRtcContextJsSIP.sendDTMF(activeSession.remoteNumber, input)
        }
    }

    const dtmfDialer = activeSession && activeSession.sessionStatus === CONNECTED

    const handleHangUp = (number) => {
        if (startConference === number) {
            setStartConference(null)
        }
        if (startTransfer === number) {
            setStartTransfer(null)
        }

        webRtcContextJsSIP.hangupSession(number)
    }

    const handleAcceptCall = (number) => {
        webRtcContextJsSIP.answerCall(number)
    }

    const toggleHoldCall = (number) => {
        webRtcContextJsSIP.toggleHoldSession(number)
    }

    const toggleRecordCall = (number) => {
        webRtcContextJsSIP.recordSession(number)
    }

    const toggleMuteCall = (number) => {
        webRtcContextJsSIP.toggleMuteSession(number)
    }

    const mergeAllCalls = () => {
        webRtcContextJsSIP.mergeSessions()
    }

    const enableForceDialPad = () => {
        setForceDialpad(true);
    }

    const disableForceDialPad = () => {
        setForceDialpad(false);
    }

    const initiateMergeCall = (number) => {
        toggleHoldCall(activeSession.remoteNumber)
        setStartConference(number)
    }

    const cancelMergeCall = () => {
        toggleHoldCall(activeSession.remoteNumber)
        setStartConference(null)
    }

    const initiateTransferCall = (number) => {
        toggleHoldCall(activeSession.remoteNumber)
        setStartTransfer(number)
    }

    const cancelTransferCall = () => {
        toggleHoldCall(activeSession.remoteNumber)
        setStartTransfer(null)
    }

    // Status pill: coloured dot, state, and the call timer once connected.
    const renderPill = (session) => (
        <div className={cx(classes.pill, toneClass(session.sessionStatus))}>
            <span className={classes.dot} />
            <span>{statusText(session.sessionStatus)}</span>
            {callTime(session) && <span className={classes.pillExtra}>{callTime(session)}</span>}
        </div>
    );

    // A round in-call button with its label underneath.
    const renderControl = ({ key, label, icon, onClick, disabled = false, on = false, recording = false }) => (
        <div key={key} className={classes.control}>
            <IconButton
                aria-label={key}
                aria-pressed={on || recording}
                className={cx(classes.controlButton, on && classes.controlButtonOn, recording && classes.controlButtonRecording)}
                onClick={onClick}
                disabled={disabled}
                size="large">
                {icon}
            </IconButton>
            <span className={cx(classes.controlLabel, disabled && classes.controlLabelDisabled)}>{label}</span>
        </div>
    );

    // Answer / hang up buttons with labels.
    const renderCallButtons = (session, endLabel) => (
        <div className={classes.callButtons}>
            <div className={classes.callButton}>
                <IconButton
                    aria-label="end"
                    className={classes.hangup}
                    onClick={() => handleHangUp(session.remoteNumber)}
                    size="large">
                    <CallEndIcon />
                </IconButton>
                <span className={classes.controlLabel}>{session.sessionStatus === INCOMING ? "Decline" : endLabel}</span>
            </div>
            {session.sessionStatus === INCOMING && (
                <div className={classes.callButton}>
                    <IconButton
                        aria-label="accept"
                        className={classes.answer}
                        onClick={() => handleAcceptCall(session.remoteNumber)}
                        size="large">
                        <CallIcon />
                    </IconButton>
                    <span className={classes.controlLabel}>Answer</span>
                </div>
            )}
        </div>
    );

    // A call that is not the main one: on hold, or parked above the keypad.
    const renderInactive = (item) => {
        return (
            <div key={item.remoteNumber} className={cx(classes.ribbon, toneClass(item.sessionStatus))}>
                {renderSessionAvatar(item, false)}
                <div className={classes.ribbonInfo}>
                    <div className={classes.ribbonName}>
                        {sessionName(item)}
                    </div>
                    <div className={classes.ribbonMeta}>
                        <span className={classes.dot} />
                        <span>{statusText(item.sessionStatus)}</span>
                        {callTime(item) && <span className={classes.pillExtra}>{callTime(item)}</span>}
                        {sessionNumber(item) && <span className={classes.pillExtra}>{sessionNumber(item)}</span>}
                    </div>
                </div>
                <div className={classes.ribbonActions}>
                    {startConference &&
                        <Tooltip title="Cancel adding a call">
                            <IconButton
                                aria-label="cancelMerge"
                                className={classes.ribbonButton}
                                onClick={() => { cancelMergeCall() }}
                                size="large">
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    }
                    {startTransfer &&
                        <Tooltip title="Cancel transfer">
                            <IconButton
                                aria-label="cancelTransfer"
                                className={classes.ribbonButton}
                                onClick={() => { cancelTransferCall() }}
                                size="large">
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    }
                    {forceDialPad && item.sessionStatus === CONNECTED &&
                        <Tooltip title="Hide keypad">
                            <IconButton
                                aria-label="forceDialPadOff"
                                className={classes.ribbonButton}
                                onClick={() => { disableForceDialPad() }}
                                size="large">
                                <KeyboardArrowDownIcon />
                            </IconButton>
                        </Tooltip>
                    }
                    {
                        (item.sessionStatus === CONNECTED || item.sessionStatus === ON_HOLD) &&
                        <Tooltip title={item.sessionStatus === ON_HOLD ? "Resume" : "Hold"}>
                            <IconButton
                                aria-label="hold"
                                className={classes.ribbonButton}
                                onClick={() => { toggleHoldCall(item.remoteNumber) }}
                                size="large">
                                {item.sessionStatus === ON_HOLD
                                    ? <PlayArrowIcon />
                                    : <PauseIcon />}
                            </IconButton>
                        </Tooltip>
                    }
                    {
                        !startTransfer && !startConference && item.sessionStatus === ON_HOLD && sipSessions.length > 1 &&
                        <Tooltip title="Merge calls">
                            <IconButton
                                aria-label="mergeCall"
                                className={classes.ribbonButton}
                                onClick={() => { mergeAllCalls() }}
                                size="large">
                                <CallMergeIcon />
                            </IconButton>
                        </Tooltip>
                    }
                    {item.sessionStatus === INCOMING &&
                        <Tooltip title="Answer">
                            <IconButton
                                aria-label="answer"
                                className={classes.ribbonAnswer}
                                onClick={() => { handleAcceptCall(item.remoteNumber) }}
                                size="large">
                                <CallIcon />
                            </IconButton>
                        </Tooltip>}
                    <Tooltip title={item.sessionStatus === INCOMING ? "Decline" : "End call"}>
                        <IconButton
                            aria-label="hangup"
                            className={classes.ribbonHangup}
                            onClick={() => handleHangUp(item.remoteNumber)}
                            size="large">
                            <CallEndIcon />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>
        );
    }

    // The main call.
    const renderActive = () => {
        const status = activeSession.sessionStatus;
        const connected = status === CONNECTED;
        const answered = connected || status === ON_HOLD;
        const busy = activeSession.recording;
        const twoCalls = sipSessions.length >= 2;

        return (
            <div key={activeSession.remoteNumber} className={cx(classes.card, toneClass(status))}>
                <div className={classes.avatarWrap}>
                    {renderSessionAvatar(activeSession, true)}
                </div>
                <div className={classes.name}>{sessionName(activeSession)}</div>
                {sessionNumber(activeSession) && (
                    <div className={classes.number}>{sessionNumber(activeSession)}</div>
                )}
                {renderPill(activeSession)}

                {answered && (
                    <div className={classes.controls}>
                        {renderControl({
                            key: "mute",
                            label: activeSession.muted ? "Unmute" : "Mute",
                            icon: activeSession.muted ? <MicOffIcon /> : <MicIcon />,
                            onClick: () => toggleMuteCall(activeSession.remoteNumber),
                            disabled: !connected,
                            on: !!activeSession.muted,
                        })}
                        {renderControl({
                            key: "dialpad",
                            label: "Keypad",
                            icon: <DialpadIcon />,
                            onClick: () => enableForceDialPad(),
                        })}
                        {renderControl({
                            key: "hold",
                            label: status === ON_HOLD ? "Resume" : "Hold",
                            icon: status === ON_HOLD ? <PlayArrowIcon /> : <PauseIcon />,
                            onClick: () => toggleHoldCall(activeSession.remoteNumber),
                            disabled: busy,
                            on: status === ON_HOLD,
                        })}
                        {connected && renderControl({
                            key: "add",
                            label: "Add call",
                            icon: <AddIcCallIcon />,
                            onClick: () => initiateMergeCall(activeSession.remoteNumber),
                            disabled: busy || twoCalls,
                        })}
                        {connected && renderControl({
                            key: "tranfer",
                            label: "Transfer",
                            icon: <PhoneForwardedIcon />,
                            onClick: () => initiateTransferCall(activeSession.remoteNumber),
                            disabled: busy || twoCalls,
                        })}
                        {connected && renderControl({
                            key: "record",
                            label: activeSession.recording ? "Stop" : "Record",
                            icon: activeSession.recording ? <SquareIcon /> : <DotIcon />,
                            onClick: () => toggleRecordCall(activeSession.remoteNumber),
                            recording: !!activeSession.recording,
                        })}
                    </div>
                )}

                {renderCallButtons(activeSession, status <= RINGING ? "Cancel" : "End call")}
            </div>
        );
    }

    // Two calls merged into one conversation.
    const renderConference = () => {
        const first = conferenceSessions[0];
        const status = first.sessionStatus;
        return (
            <div className={cx(classes.card, toneClass(status))}>
                <div className={cx(classes.avatarWrap, classes.avatarGroup)}>
                    {conferenceSessions.map((item) => (
                        <React.Fragment key={item.remoteNumber}>
                            {renderSessionAvatar({ ...item, sessionStatus: status }, true)}
                        </React.Fragment>
                    ))}
                </div>
                <div className={classes.name}>Conference</div>
                <div className={classes.number}>
                    {conferenceSessions.map((item) => sessionName(item)).join(" · ")}
                </div>
                {renderPill(first)}

                {(status === CONNECTED || status === ON_HOLD) && (
                    <div className={classes.controls}>
                        {renderControl({
                            key: "mute",
                            label: first.muted ? "Unmute" : "Mute",
                            icon: first.muted ? <MicOffIcon /> : <MicIcon />,
                            onClick: () => toggleMuteCall(first.remoteNumber),
                            disabled: status !== CONNECTED,
                            on: !!first.muted,
                        })}
                        {renderControl({
                            key: "hold",
                            label: status === ON_HOLD ? "Resume" : "Hold",
                            icon: status === ON_HOLD ? <PlayArrowIcon /> : <PauseIcon />,
                            onClick: () => toggleHoldCall(first.remoteNumber),
                            on: status === ON_HOLD,
                        })}
                    </div>
                )}

                {renderCallButtons(first, "End conference")}
            </div>
        );
    }

    const handleCallerCallClick = () => {
        disableForceDialPad()
        if (voip.active === 'yes') {
            handleSipDialClick()
        } else {
            handleDialClick()
        }
    }

    // The latest calls, one per number, shown beside the keypad while idle.
    // Choosing one puts its number in the dial field; it does not dial.
    const RECENT_COUNT = 6;
    const recentCalls = (() => {
        const seen = new Set();
        return [...(calls || [])]
            .filter((item) => String(item.number || "").trim() !== "")
            .sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime))
            .filter((item) => {
                const key = dialDigits(item.number).replace(/^1(?=\d{10}$)/, "");
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            })
            .slice(0, RECENT_COUNT);
    })();

    const fillDialer = (number) => {
        const text = String(number || "");
        setViewValue(text);
        setValue(getPhoneBookEntry(phonebook || [], dialDigits(text)));
    };

    const renderRecent = (item) => {
        const serverName = String(item.name || "").trim();
        const name = serverName !== ""
            ? serverName
            : contactDisplayName(findContactByNumber([personalPhonebook || []], item.number));
        const number = String(formatUsPhone(item.number) || "");
        const outgoing = item.direction === "outgoing";
        const unanswered = String(item.state || "").toLowerCase().replace(/\s/g, "") !== "answered";
        const missed = !outgoing && unanswered;
        const Icon = missed ? CallMissedIcon : outgoing ? (unanswered ? CallMissedOutgoingIcon : CallMadeIcon) : CallReceivedIcon;
        const tone = missed ? "danger" : outgoing ? (unanswered ? "neutral" : "brand") : "success";
        return (
            <ButtonBase
                key={`${item.jid || item.number}-${item.dateTime}`}
                className={classes.recentItem}
                onClick={() => fillDialer(item.number)}
                aria-label={`dial ${name !== "" ? name : number}`}
            >
                <ListParty
                    icon={<Icon />}
                    tone={tone}
                    title={name !== "" ? name : number}
                    detail={name !== "" ? number : (missed ? "Missed call" : outgoing ? "Outgoing" : "Incoming")}
                    detailDanger={missed}
                />
                <span className={classes.recentWhen}>{formatWhen(item.dateTime)}</span>
            </ButtonBase>
        );
    };

    const showDialer = (!activeSession && conferenceSessions.length === 0) || startConference || startTransfer || forceDialPad;
    const showActive = activeSession && !startConference && !startTransfer && !inConference && !forceDialPad;
    const showConference = !startConference && !startTransfer && inConference;

    // What the keypad is for, when it is shown during a call.
    const dialerMode = startTransfer
        ? { title: "Transfer call", hint: "Dial the number to transfer this call to." }
        : startConference
            ? { title: "Add a call", hint: "Dial the number to add to this call." }
            : forceDialPad
                ? { title: "Keypad", hint: "The keys you press are sent to the call." }
                : null;

    return (
        <Paper className={classes.paper}>
            <Toolbar
                className={clsx(classes.toolbarRoot, classes.colorDefault)}
            >
                <Typography
                    className={classes.title}
                    variant="h6"
                    id="tableTitle"
                    component="div"
                >
                    {title}
                </Typography>
            </Toolbar>
            <div className={classes.main}>
                {inactiveSessions.length > 0 && (
                    <div className={classes.ribbons}>
                        {inactiveSessions.map((item) => renderInactive(item))}
                    </div>
                )}
                <div className={classes.stage}>
                    {showActive && renderActive()}
                    {showConference && renderConference()}
                    {showDialer && (
                        <div className={cx(classes.card, classes.cardDialer)}>
                            {dialerMode && (
                                <div className={classes.modeHeader}>
                                    <div className={classes.modeTitle}>{dialerMode.title}</div>
                                    <div className={classes.modeHint}>{dialerMode.hint}</div>
                                </div>
                            )}
                            <CallDialer
                                calling={voip.active === 'yes' ? sipCalling : calling}
                                setCalling={voip.active === 'yes' ? setSipCalling : setCalling}
                                mode={voip.active === 'yes' ? sipMode : "newCall"}
                                options={options}
                                value={value}
                                setValue={setValue}
                                viewValue={viewValue}
                                setViewValue={setViewValue}
                                controller={controller}
                                handleDialClick={handleCallerCallClick}
                                deviceSelection={deviceSelection}
                                setDeviceSelection={setDeviceSelection}
                                selectedIndex={selectedIndex}
                                setSelectedIndex={setSelectedIndex}
                                dialerInputCb={dialerInputCb}
                                dtmfDialer={dtmfDialer}
                                history={history}
                                transferCall={startTransfer}
                            />
                        </div>
                    )}
                    {showDialer && !dialerMode && sipSessions.length === 0 && (
                        <div className={classes.recent}>
                            <div className={classes.recentHeader}>
                                <span className={classes.recentTitle}>Recent</span>
                                <ButtonBase className={classes.recentAll} onClick={() => navigate('/dashboard/call/journal/all')}>
                                    Call journal
                                </ButtonBase>
                            </div>
                            {recentCalls.length > 0
                                ? recentCalls.map((item) => renderRecent(item))
                                : <div className={classes.recentEmpty}>No recent calls.</div>}
                        </div>
                    )}
                </div>
            </div>
        </Paper>
    );
}

const mapStateToProps = (state) => {
    return {
        session: state.session,
        user: state.user,
        phonebook: state.phonebook,
        personalPhonebook: state.personalPhonebook,
        externalPhonebook: state.externalPhonebook,
        calls: state.calls,
        startConference: state.user.startConference,
        startTransfer: state.user.startTransfer,
        sipSessions: state.user.sipSessions,
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(Phone);
